import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { Form, Icon, Modal, notification, Typography } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'

import { Creators as LoadingCreators } from '../../../../../../appRedux/ducks/loading'
import { getMessage } from '../../../../../../util/IntlMessages'
import { isAdmin } from '../../../../../../util/profiles'
import { actionStatus, actionStatusMap } from '../../constants'
import { useCreateActionContext } from '../../contexts/action'
import {
  useCheckIfDuplicatedActionExistsMutation,
  useCreateActionMutation,
  useGetActionQuery,
  useUpdateActionMutation
} from '../../services/action'
import { useGetHarvestsQuery } from '../../services/field'
import { DuplicatedModalAlert } from './DuplicatedModalAlert'

import './styles.less'

const DEFAULT_ACTION_STATUS_ID = actionStatusMap.get(actionStatus.PLANNED)
const EMPTY_ACTION_DATA = { baseFields: {}, customFields: {} }

export function ActionForm({ children }) {
  const history = useHistory()
  const dispatch = useDispatch()

  const { actionId, isNewAction, setIsReadOnly, isPreview, setIsPreview, validationSchema, beginSubmit, endSubmit } =
    useCreateActionContext()

  const { data: actionData = EMPTY_ACTION_DATA } = useGetActionQuery({ actionId }, { skip: isNewAction })
  const initialExecutionDate = useRef(moment())
  const formKey = useMemo(
    () => (isNewAction ? 'new-action' : String(actionId ?? 'new-action')),
    [actionId, isNewAction]
  )

  const { data: harvestsList = [], isLoading: isLoadingHarvests } = useGetHarvestsQuery()

  const currentHarvest = useMemo(() => {
    return harvestsList?.find((harvest) => harvest.isCurrent === true)
  }, [harvestsList])

  const [createAction] = useCreateActionMutation()
  const [updateAction] = useUpdateActionMutation()
  const [checkIfDuplicatedActionExists] = useCheckIfDuplicatedActionExistsMutation()

  const [duplicateModal, setDuplicateModal] = useState({ visible: false, duplicateId: null, payload: null })

  const isForecastPreload = location.state?.source === 'forecast-visit-modal'
  const preloadActionData = isForecastPreload ? location.state?.preloadActionData : null

  const initialFormValues = useMemo(() => {
    if (!isNewAction) {
      return actionData
    }

    if (isForecastPreload) {
      return {
        baseFields: {
          ...(actionData?.baseFields || {}),
          ...(preloadActionData?.baseFields || {}),
          executionDate: preloadActionData?.baseFields?.executionDate
            ? moment(preloadActionData.baseFields.executionDate)
            : null
        },
        customFields: {
          ...(actionData?.customFields || {}),
          ...(preloadActionData?.customFields || {})
        }
      }
    }

    return {
      baseFields: {
        ...(actionData?.baseFields || {}),
        executionDate: initialExecutionDate.current,
        actionStatusId: DEFAULT_ACTION_STATUS_ID,
        referencePeriodId: currentHarvest?.value || null
      },
      customFields: {
        ...(actionData?.customFields || {})
      }
    }
  }, [isNewAction, isForecastPreload, preloadActionData, actionData, currentHarvest])

  useEffect(
    function checkIsReadOnly() {
      if (!actionData?.baseFields?.actionStatusId) return
      const { baseFields } = actionData

      if (baseFields.actionStatusId === actionStatusMap.get(actionStatus.DONE) && !isAdmin()) {
        setIsReadOnly(true)
      }
    },
    [actionData?.baseFields]
  )

  /*
    [AD-14811] Sempre chamada com o lock do contexto ja adquirido -- pelo clique no ActionHeader ou pelo
    wrapper `confirmActionFromGesture` abaixo. Por isso nao verifica nem adquire o lock aqui: fazer isso
    bloquearia o proprio fluxo que acabou de travar.
  */
  async function confirmAction(payload, checkDuplicated) {
    dispatch(LoadingCreators.openLoading(payload.baseFields?.finish))

    try {
      if (isNewAction) {
        if (checkDuplicated) {
          const { data: duplicateData } = await checkIfDuplicatedActionExists({ payload })
          if (duplicateData?.id) {
            setDuplicateModal({ visible: true, duplicateId: duplicateData.id, payload })
            return
          }
        }

        const { data } = await createAction({ payload })
        if (data?.id) {
          history.push(`/actions/${payload.baseFields.actionTypeId}/${data.id}`)
        }
      } else {
        await updateAction({ actionId, payload })
      }
    } finally {
      dispatch(LoadingCreators.closeLoading())
    }
  }

  /*
    [AD-14811] Os dois gestos que salvam sem passar pelo botao do header -- o OK do Modal.confirm de
    finalizacao e o "salvar mesmo assim" do modal de duplicata -- adquirem o lock por conta propria, porque
    o clique original ja liberou o dele ao abrir o modal.
  */
  async function confirmActionFromGesture(payload, checkDuplicated) {
    if (!beginSubmit()) return

    try {
      await confirmAction(payload, checkDuplicated)
    } finally {
      endSubmit()
    }
  }

  async function onSubmit({ baseFields, customFields }) {
    const payload = {
      customFields,
      baseFields: {
        ...baseFields,
        mobile: false,
        executionDate: baseFields?.executionDate ? moment(baseFields.executionDate).format('MM/DD/YYYY') : null,
        actionPlannedDoneTime: baseFields?.executionDate ? moment(baseFields.executionDate) : null
        /*
          [Suporte #46638] Visitas - Remover campo "Hora" do formulário principal
          USER STORY 10613 (AzureDevops)
        */
        // executionTime: baseFields?.executionTime ? moment(baseFields.executionTime, 'HH:mm').format('HH:mm') : null
      }
    }

    if (payload.baseFields?.finish && payload.baseFields.executionDate == null) {
      return notification.warn({
        description: getMessage('validationNotEmptyDateFinishActions'),
        message: getMessage('sweetAlerts.warning')
      })
    }

    if (payload.baseFields?.finish && moment(payload.baseFields.executionDate).isAfter(new Date())) {
      return notification.warn({
        description: getMessage('validationFutureDateFinishActions'),
        message: getMessage('sweetAlerts.attention')
      })
    }

    if (
      (payload.baseFields?.finish || payload.baseFields?.actionStatusId === actionStatusMap.get(actionStatus.DONE)) &&
      !isPreview
    ) {
      return setIsPreview(true)
    }

    if (payload.baseFields?.finish) {
      return Modal.confirm({
        title: getMessage('confirmActionFinish'),
        okText: 'Confirmar',
        cancelText: 'Cancelar',
        icon: null,
        onOk: () => confirmActionFromGesture(payload, true),
        onCancel: () => {}
      })
    }

    await confirmAction(payload, isNewAction)
  }

  // Wait for harvests to load before rendering form for new actions
  if (isNewAction && isLoadingHarvests) {
    return (
      <div className="customFieldsList">
        <Icon type="loading" className="customFieldsList__loadingIcon" spin />
        <Typography className="customFieldsList__loadingText">{getMessage('loading...')}</Typography>
      </div>
    )
  }

  function handleGoToDuplicate() {
    const { duplicateId, payload: storedPayload } = duplicateModal
    setDuplicateModal({ visible: false, duplicateId: null, payload: null })
    history.push(`/actions/${storedPayload.baseFields.actionTypeId}/${duplicateId}`)
  }

  function handleSaveAnyway() {
    const { payload: storedPayload } = duplicateModal
    setDuplicateModal({ visible: false, duplicateId: null, payload: null })
    confirmActionFromGesture(storedPayload)
  }

  function handleCloseDuplicateModal() {
    setDuplicateModal({ visible: false, duplicateId: null, payload: null })
  }

  return (
    <>
      <Formik
        key={formKey}
        enableReinitialize
        initialValues={initialFormValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit} layout="vertical" className="createActionForm">
            {children}
          </Form>
        )}
      </Formik>

      <Modal visible={duplicateModal.visible} onCancel={handleCloseDuplicateModal} footer={null} centered>
        <DuplicatedModalAlert handleSaveAnyway={handleSaveAnyway} handleGoToDuplicate={handleGoToDuplicate} />
      </Modal>
    </>
  )
}
